import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

const STATUS_LABEL = {
    active: "Aktiv", published: "Aktiv", draft: "Entwurf", neu: "Neu", new: "Neu",
    reserved: "Reserviert", reserviert: "Reserviert", sold: "Verkauft", verkauft: "Verkauft",
    open: "Offen", offen: "Offen", confirmed: "Bestätigt", in_progress: "In Arbeit", done: "Erledigt",
    paid: "Bezahlt", bezahlt: "Bezahlt", overdue: "Überfällig", cancelled: "Storniert",
    won: "Gewonnen", lost: "Verloren", assigned: "Zugewiesen", invoiced: "Berechnet",
};

const fmt = (v, type) => {
    if (v === null || v === undefined || v === "") return "—";
    if (type === "price") return formatPrice(v);
    if (type === "num") return Number(v).toLocaleString("de-DE");
    if (type === "date") {
        const d = new Date(v);
        return isNaN(d) ? String(v) : d.toLocaleDateString("de-DE");
    }
    if (type === "status") return STATUS_LABEL[v] || String(v);
    return String(v);
};

/**
 * CompanyModulePanel — generische Read-Only-Tabelle für ein freigegebenes
 * Firmen-Modul. Bei `editable` Modulen (Anfragen, Werkstatt) zeigt sie pro Zeile
 * einen „Bearbeiten"-Dialog (Status + Notizen, rechte-geprüft, mit Audit-Log).
 */
export default function CompanyModulePanel({ dealerId, module }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [editRow, setEditRow] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const fetchData = () => {
        setData(null); setError(null);
        return api.get(`/vermittler/company/${dealerId}/module/${module}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Daten konnten nicht geladen werden"));
    };

    useEffect(() => {
        let alive = true;
        setData(null); setError(null);
        api.get(`/vermittler/company/${dealerId}/module/${module}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) setError(e.response?.data?.detail || "Daten konnten nicht geladen werden"); });
        return () => { alive = false; };
    }, [dealerId, module]);

    const openEdit = (row) => {
        const cfg = data.edit_config || {};
        const f = {};
        if (cfg.status_options) f.status = row.status;
        (cfg.text_fields || []).forEach((tf) => { f[tf.key] = row[tf.key] || ""; });
        (cfg.number_fields || []).forEach((nf) => { f[nf.key] = row[nf.key] ?? ""; });
        setForm(f);
        setEditRow(row);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/vermittler/company/${dealerId}/${module}/${editRow.id}`, form);
            toast.success("Änderung gespeichert");
            setEditRow(null);
            await fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    if (error) {
        return <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="company-module-error">{error}</div>;
    }
    if (!data) {
        return <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>;
    }
    if (data.items.length === 0) {
        return <div className="text-sm text-gray-500 text-center py-6" data-testid="company-module-empty">Keine Einträge vorhanden.</div>;
    }

    const cfg = data.edit_config || {};

    return (
        <div className="border border-gray-200 rounded-sm overflow-hidden" data-testid={`company-module-table-${module}`}>
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <h3 className="font-display font-bold text-sm">{data.title}</h3>
                {data.editable && <span className="text-[10px] uppercase tracking-wider font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-sm">Bearbeitbar</span>}
                <span className="ml-auto text-xs text-gray-500 font-mono-num" data-testid={`company-module-count-${module}`}>{data.count} Einträge</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-left">
                            {data.columns.map((c) => (
                                <th key={c.key} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-gray-500 ${c.type === "price" || c.type === "num" ? "text-right" : ""}`}>{c.label}</th>
                            ))}
                            {data.editable && <th className="px-3 py-2" />}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.items.slice(0, 100).map((row, i) => (
                            <tr key={row.id || i} className="hover:bg-gray-50" data-testid={`company-module-row-${module}-${i}`}>
                                {data.columns.map((c) => (
                                    <td key={c.key} className={`px-3 py-2 ${c.type === "price" || c.type === "num" ? "text-right font-mono-num" : ""} ${c.key === data.columns[0].key ? "font-bold text-[#111827]" : "text-gray-700"}`}>
                                        {c.type === "status" ? (
                                            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-700">{fmt(row[c.key], c.type)}</span>
                                        ) : fmt(row[c.key], c.type)}
                                    </td>
                                ))}
                                {data.editable && (
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            onClick={() => openEdit(row)}
                                            className="inline-flex items-center gap-1 text-xs font-bold text-[#111827] hover:text-[#FFCC00]"
                                            data-testid={`company-module-edit-${module}-${i}`}
                                        >
                                            <Pencil className="w-3.5 h-3.5" /> Bearbeiten
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
                <DialogContent data-testid="company-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>{data.title} bearbeiten</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {cfg.notice && (
                            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2" data-testid="company-edit-notice">
                                {cfg.notice}
                            </div>
                        )}
                        {cfg.status_options && (
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                                    <SelectTrigger className="mt-1 rounded-sm" data-testid="company-edit-status">
                                        <SelectValue placeholder="Status wählen" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cfg.status_options.map((o) => (
                                            <SelectItem key={o.value} value={o.value} data-testid={`company-edit-status-${o.value}`}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {(cfg.number_fields || []).map((nf) => (
                            <div key={nf.key}>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{nf.label}</Label>
                                <Input
                                    type="number"
                                    step={nf.step || "1"}
                                    min="0"
                                    value={form[nf.key] ?? ""}
                                    onChange={(e) => setForm((f) => ({ ...f, [nf.key]: e.target.value }))}
                                    className="mt-1 rounded-sm font-mono-num"
                                    data-testid={`company-edit-field-${nf.key}`}
                                />
                            </div>
                        ))}
                        {(cfg.text_fields || []).map((tf) => (
                            <div key={tf.key}>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{tf.label}</Label>
                                <Textarea
                                    value={form[tf.key] || ""}
                                    onChange={(e) => setForm((f) => ({ ...f, [tf.key]: e.target.value }))}
                                    className="mt-1 rounded-sm"
                                    rows={3}
                                    data-testid={`company-edit-field-${tf.key}`}
                                />
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditRow(null)} disabled={saving} className="rounded-sm">Abbrechen</Button>
                        <Button onClick={save} disabled={saving} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="company-edit-save">
                            {saving ? "Speichert…" : "Speichern"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
